import * as Sentry from '@sentry/vue';
import { createApp, type Plugin } from 'vue';
import App from './App.vue';

// ROUTER (customer-specific)
import router from './router';

// STYLES
import './theme/customer-theme.scss'; // Customer-specific theme

// CUSTOMER CONFIG (customer-specific links, assets, etc.)
import {
  appConfig,
  getFeatureConfigOverridesFromAppConfig,
  ignoreExpectedLivenessSentryEvents,
  registerSentryObservability,
  useAuthSessionStore,
  useAuthStore,
  useCustomerConfigStore,
  useFeatureManagerStore,
} from '@peanuds/app-shell';
import { configureApiBaseUrl, registerApiAuthHandlers } from '@peanuds/shared';
import { customerConfig } from './customer-config';
import { featureConfig } from './feature-config';

// PLUGINS
import Auth0Plugin from '@peanuds/app-shell/plugins/auth0';
import HubSpotPlugin from '@peanuds/app-shell/plugins/hubspot';
import { appDe, appEn, createI18nPlugin } from '@peanuds/app-shell/plugins/i18n';
import PiniaPlugin from '@peanuds/app-shell/plugins/pinia';
import { createPrimeVuePlugin } from '@peanuds/app-shell/plugins/prime-vue';
import SwiperPlugin from '@peanuds/app-shell/plugins/swiper';
import { bebawaPrimeVuePreset } from './theme/primevue-preset';

// BASE TRANSLATIONS (@peanuds/app-shell as foundation)
// App-shell contains the comprehensive list of common strings
const baseEn = appEn;
const baseDe = appDe;

// CUSTOMER LOCALES (Customer-specific app-level translations)
// These override or extend app-shell strings for this customer
import customerDe from './locales/de.json';
import customerEn from './locales/en.json';

// FEATURE LOCALES (Feature-specific strings from @peanuds/*)
// These are merged after customer translations to avoid conflicts
import { analyticsLocales } from '@peanuds/analytics';
import { cardsLocales } from '@peanuds/cards';
import { contactsLocales } from '@peanuds/contacts';
import { controlSpendingLocales } from '@peanuds/control-spending';
import { dataExportLocales } from '@peanuds/data-export';
import { exchangeLocales } from '@peanuds/exchange';
import { accountsLocales } from '@peanuds/accounts';
import { ibanSwiperLocales, transactionDetailsLocales } from '@peanuds/modules';
import { moneyLocales } from '@peanuds/money';
import { paymentsApprovalLocales } from '@peanuds/payments-approval';
import { batchCollectionsLocales } from '@peanuds/batch-collections';
import { settingsLocales } from '@peanuds/settings';
import { totalBalanceLocales } from '@peanuds/total-balance';
import { uiLocales } from '@peanuds/ui';

/**
 * I18N Configuration with Proper Merge Order
 *
 * Merge order (first → last, last wins on conflicts):
 * 1. Base translations (@peanuds/app-shell - app chrome & common strings)
 * 2. Customer locales (customer-specific overrides for this app instance)
 * 3. Feature locales (feature-specific strings)
 *
 * This ensures:
 * - Customer can override app-shell strings
 * - Features can extend both
 * - No accidental key collisions (clear override hierarchy)
 */
const I18nPlugin = createI18nPlugin({
  baseTranslations: { en: baseEn, de: baseDe },
  featureLocales: [
    // 1. Customer-specific locales (overrides for this customer)
    { en: customerEn, de: customerDe },
    // 2. Feature locales (domain-specific strings)
    analyticsLocales,
    batchCollectionsLocales,
    cardsLocales,
    contactsLocales,
    controlSpendingLocales,
    dataExportLocales,
    ibanSwiperLocales,
    accountsLocales,
    exchangeLocales,
    moneyLocales,
    paymentsApprovalLocales,
    settingsLocales,
    totalBalanceLocales,
    transactionDetailsLocales,
    uiLocales,
  ],
});

const startApp = (authPlugin: Plugin) => {
  const app = createApp(App);
  const PrimePlugin = createPrimeVuePlugin({
    preset: bebawaPrimeVuePreset,
    dateLocale: customerConfig.dateTime?.locale,
  });

  app.use(PiniaPlugin);

  if (appConfig.VITE_PUBLIC_SENTRY_DSN) {
    Sentry.init({
      app,
      dsn: appConfig.VITE_PUBLIC_SENTRY_DSN,
      environment: appConfig.VITE_ENV_NAME,
      integrations: [Sentry.browserTracingIntegration({ router })],
      beforeSend: ignoreExpectedLivenessSentryEvents,
      tracesSampleRate: appConfig.VITE_ENV_NAME === 'prod' ? 0.1 : 1.0,
    });

    registerSentryObservability({
      getIdentity: () => {
        const authSessionStore = useAuthSessionStore();

        return {
          userId: authSessionStore.ifExternalUserId ?? null,
          organizationId: authSessionStore.organizationId,
          ifExternalUserId: authSessionStore.ifExternalUserId,
          ifClientId: authSessionStore.ifClientId,
          sessionStatus: authSessionStore.status,
          isTokenExpired: authSessionStore.isTokenExpired,
        };
      },
    });
  }

  configureApiBaseUrl(appConfig.VITE_API_URL);

  // SETUP
  registerApiAuthHandlers({
    getAuthSession: () => useAuthSessionStore(),
    redirectToLogin: () => useAuthStore().redirectToLogin({ mode: 'resume' }),
  });

  // Initialize customer config store with env values
  // (can be updated from API later)
  const customerConfigStore = useCustomerConfigStore();
  customerConfigStore.initialize(customerConfig);

  const featureManagerStore = useFeatureManagerStore();
  const featureConfigOverrides = getFeatureConfigOverridesFromAppConfig(appConfig);
  featureManagerStore.initialize({
    flags: featureConfig.flags,
    customizations: featureConfig.customizations,
    overrides: featureConfigOverrides.flags,
    customizationOverrides: featureConfigOverrides.customizations,
  });

  app.use(authPlugin);
  app.use(router);
  app.use(I18nPlugin);
  app.use(PrimePlugin);
  app.use(SwiperPlugin);
  app.use(HubSpotPlugin);

  app.mount('#app');
};

const startE2eApp = async () => {
  const { startE2eMockWorker } = await import('../../../src/mocks/server');

  await startE2eMockWorker();

  const { default: authPlugin } = await import('@peanuds/app-shell/plugins/e2e-auth');

  startApp(authPlugin);
};

// Keep this gate statically foldable so real production builds exclude E2E code.
if (
  import.meta.env.VITE_AUTH_MODE === 'e2e' &&
  (import.meta.env.VITE_ENV_NAME === 'demo' ||
    (import.meta.env.DEV &&
      (import.meta.env.VITE_ENV_NAME === 'local' || import.meta.env.VITE_ENV_NAME === 'dev')))
) {
  void startE2eApp();
} else {
  startApp(Auth0Plugin);
}
